import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { ChatHeader } from './ChatHeader';
import { ChatMessageList } from './ChatMessageList';
import { ChatInput } from './ChatInput';
import './ChatBot.css';

interface ChatWindowProps {
  sessionId: string | null;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  className?: string;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  sessionId,
  isConnected,
  connectionStatus,
  className = ''
}) => {
  const {
    isMinimized,
    messages,
    isLoading,
    isTyping
  } = useSelector((state: RootState) => state.chatBot);

  const [isAnimatingIn, setIsAnimatingIn] = useState(true);

  // Reserve space for chatbot - only adjust right margin to prevent table shrinking
  useEffect(() => {
    const wrapper = document.querySelector('.wrapper') as HTMLElement;
    const chatbotWidth = 420;

    // FIX: Store timeout references to prevent memory leak
    let openTimeout: NodeJS.Timeout | null = null;

    if (wrapper) {
      // Set transition timing to match exactly
      wrapper.style.transition = 'margin-right 0.5s cubic-bezier(0.4, 0, 0.2, 1)';

      // Small delay to ensure both animations start together
      openTimeout = setTimeout(() => {
        setIsAnimatingIn(false);
        if (wrapper) {
          wrapper.style.marginRight = `${chatbotWidth}px`;
          wrapper.style.boxSizing = 'border-box';
        }
      }, 10);
    }

    // Restore on unmount with transition
    return () => {
      // FIX: Clear any pending timeouts to prevent memory leak
      if (openTimeout) clearTimeout(openTimeout);

      if (wrapper) {
        // Don't use setTimeout in cleanup - component is unmounting
        // Just reset styles immediately
        wrapper.style.transition = '';
        wrapper.style.marginRight = '';
        wrapper.style.boxSizing = '';
      }
    };
  }, []);

  // Service Error Screen Component - Full screen error when service is completely down
  const ServiceErrorScreen = () => (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 24px',
      textAlign: 'center',
      gap: '24px'
    }}>
      {/* Robot Image */}
      <div style={{
        width: '100px',
        height: '100px',
        animation: 'gentle-bounce 2s ease-in-out infinite'
      }}>
        <img
          src="./assets/react-components/robot-maintenance.svg"
          alt="Robot under maintenance"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: 0.9
          }}
        />
      </div>

      {/* Error Message */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '500',
          color: '#64b5c8',
          margin: 0,
          lineHeight: '1.4',
          letterSpacing: '0.01em'
        }}>
          It's not you, it's our servers having a moment...
        </h3>
        <p style={{
          fontSize: '13px',
          color: '#94a3b8',
          margin: 0,
          lineHeight: '1.6',
          fontWeight: '400'
        }}>
          We're experiencing technical difficulties.<br/>
          Please click the chat icon to try again.
        </p>
      </div>

      {/* Status indicator */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '8px 16px',
        backgroundColor: '#e0f2f7',
        borderRadius: '8px',
        fontSize: '12px',
        color: '#64b5c8',
        fontWeight: '500'
      }}>
        <div style={{
          width: '6px',
          height: '6px',
          backgroundColor: '#64b5c8',
          borderRadius: '50%',
          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
        }}></div>
        <span>Service temporarily unavailable</span>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes gentle-bounce {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </div>
  );

  return (
    <>
      {/* Side Drawer */}
      <div
        className={className}
        style={{
          background: 'rgba(255, 255, 255, 0.98)',
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.98)),
            url('./assets/react-components/chatbot-bg.jpg')
          `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backdropFilter: 'blur(28px) saturate(108%)',
          boxShadow: '-4px 0 16px rgba(33, 155, 206, 0.12), -2px 0 8px rgba(0, 0, 0, 0.06)',
          border: '2px solid #D9F0FF',
          borderRight: 'none',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: isAnimatingIn ? 'translateX(100%)' : 'translateX(0)',
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          left: 'auto',
          width: '420px',
          height: '100vh',
          zIndex: 9999,
          borderRadius: '0',
          margin: 0,
          padding: 0
        }}
      >
      {/* Header */}
      <ChatHeader
        isConnected={isConnected}
        connectionStatus={connectionStatus}
      />

      {/* Main Chat Content - Hidden when minimized */}
      {!isMinimized && (
        <>
          {/* Messages Area */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}>
            {/* Show error screen when service is down - inside message area */}
            {(!isConnected && (connectionStatus === 'error' || connectionStatus === 'disconnected') && !sessionId && messages.length === 0) ? (
              <ServiceErrorScreen />
            ) : (
              <ChatMessageList
                messages={messages}
                isTyping={isTyping}
                sessionId={sessionId}
                isLoading={isLoading}
                className="flex-1"
              />
            )}
          </div>

          {/* Chat Input - Always visible at bottom, disabled when not connected */}
          <ChatInput
            sessionId={sessionId}
            disabled={!isConnected || connectionStatus === 'error'}
            placeholder={
              !isConnected
                ? "Service temporarily unavailable..."
                : "Ask me anything"
            }
          />
        </>
      )}

      {/* Minimized State Content */}
      {isMinimized && (
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            color: 'white'
          }}>
            {isTyping && (
              <>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <div style={{
                    width: '4px',
                    height: '4px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    animation: 'bounce 1.4s ease-in-out both infinite',
                    animationDelay: '0ms'
                  }}></div>
                  <div style={{
                    width: '4px',
                    height: '4px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    animation: 'bounce 1.4s ease-in-out both infinite',
                    animationDelay: '150ms'
                  }}></div>
                  <div style={{
                    width: '4px',
                    height: '4px',
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    animation: 'bounce 1.4s ease-in-out both infinite',
                    animationDelay: '300ms'
                  }}></div>
                </div>
                <span>Assistant is typing...</span>
              </>
            )}
            {!isTyping && isConnected && (
              <span>Chat ready</span>
            )}
            {!isConnected && (
              <span style={{ color: '#fef08a' }}>Reconnecting...</span>
            )}
          </div>
          
          {messages.length > 0 && (
            <div style={{
              fontSize: '12px',
              color: 'white',
              opacity: 0.75
            }}>
              {messages.length} message{messages.length > 1 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      </div>
    </>
  );
};

export default ChatWindow;